import { useRef, useState } from 'react';
import FormField from './FormField.jsx';
import OptionChips from './OptionChips.jsx';
import SuccessMessage from './SuccessMessage.jsx';
import { ERROR_MESSAGES, FIELD_ORDER, validate } from '../lib/validation.js';
import { submitRegistration } from '../lib/submitRegistration.js';

const CATEGORY_OPTIONS = [
  { value: 'Household', label: 'Household' },
  { value: 'Housing society / RWA', label: 'Housing society / RWA' },
  { value: 'Shop / Office', label: 'Shop / Office' },
  { value: 'Industrial unit', label: 'Industrial unit' },
  { value: 'Agricultural', label: 'Agricultural' },
  { value: 'Institution', label: 'Institution (school, hospital etc.)' },
];

const NEED_OPTIONS = [
  { value: 'Supply for my existing connection', label: 'Supply for my existing connection' },
  { value: 'New connection', label: 'New connection' },
  { value: 'Additional load', label: 'Additional load' },
];

const INITIAL_VALUES = {
  name: '',
  mobile: '',
  email: '',
  category: '',
  district: '',
  pin: '',
  locality: '',
  need: '',
  load: '',
  language: 'English',
  consent: false,
};

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export default function RegistrationForm() {
  const [values, setValues] = useState(INITIAL_VALUES);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | submitting | error | success
  const formRef = useRef(null);
  const honeypotRef = useRef(null);
  const inFlight = useRef(false);

  const update = (name, value) => {
    const next = { ...values, [name]: value };
    setValues(next);
    // As in the original: once a field is flagged, re-check as the user corrects it.
    if (errors[name]) setErrors(validate(next));
  };

  const onInput = (e) => {
    const { name, type, checked, value } = e.target;
    update(name, type === 'checkbox' ? checked : value);
  };

  const focusFirstInvalid = (errs) => {
    const first = FIELD_ORDER.find((f) => errs[f]);
    const container = formRef.current?.querySelector(`[data-field="${first}"]`);
    if (!container) return;
    container.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
    container.querySelector('input, select')?.focus({ preventScroll: true });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (inFlight.current) return;

    const errs = validate(values);
    setErrors(errs);
    if (Object.keys(errs).length) {
      focusFirstInvalid(errs);
      return;
    }
    if (honeypotRef.current?.value) return; // bot

    const payload = {
      ...values,
      consent: true,
      consent_text_version: 'v1',
      submitted_at: new Date().toISOString(),
      source: 'elevenpower.in/register',
    };

    inFlight.current = true;
    setStatus('submitting');
    try {
      await submitRegistration(payload);
      setStatus('success');
    } catch (err) {
      console.error(err);
      setStatus('error');
    } finally {
      inFlight.current = false;
    }
  };

  const err = (name) => (errors[name] ? ERROR_MESSAGES[name] : undefined);
  const submitting = status === 'submitting';

  return (
    <section className="card" aria-label="Registration form">
      {status === 'success' ? (
        <SuccessMessage />
      ) : (
        <form id="eoi" ref={formRef} onSubmit={onSubmit} noValidate>
          <fieldset className="section">
            <legend className="section__title">Your details</legend>
            <div className="grid">
              <FormField id="name" label="Full name" error={err('name')} full>
                {(a11y) => (
                  <input {...a11y} name="name" type="text" autoComplete="name" required value={values.name} onChange={onInput} />
                )}
              </FormField>
              <FormField id="mobile" label="Mobile number" error={err('mobile')}>
                {(a11y) => (
                  <input
                    {...a11y}
                    name="mobile"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel-national"
                    maxLength={10}
                    placeholder="10-digit number"
                    required
                    value={values.mobile}
                    onChange={onInput}
                  />
                )}
              </FormField>
              <FormField id="email" label="Email" optional error={err('email')}>
                {(a11y) => (
                  <input {...a11y} name="email" type="email" autoComplete="email" value={values.email} onChange={onInput} />
                )}
              </FormField>
            </div>
          </fieldset>

          <hr className="rule" />

          <fieldset className="section">
            <legend className="section__title">Your premises</legend>
            <div className="grid">
              <OptionChips
                name="category"
                legend="I am registering as"
                options={CATEGORY_OPTIONS}
                value={values.category}
                onChange={update}
                error={err('category')}
              />

              <FormField id="district" label="District" error={err('district')}>
                {(a11y) => (
                  <select {...a11y} name="district" required value={values.district} onChange={onInput}>
                    <option value="">Select</option>
                    <option>Gurugram</option>
                    <option>Nuh</option>
                  </select>
                )}
              </FormField>
              <FormField id="pin" label="PIN code" error={err('pin')}>
                {(a11y) => (
                  <input
                    {...a11y}
                    name="pin"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    autoComplete="postal-code"
                    required
                    value={values.pin}
                    onChange={onInput}
                  />
                )}
              </FormField>
              <FormField id="locality" label="Sector, colony, village or industrial area" error={err('locality')} full>
                {(a11y) => (
                  <input
                    {...a11y}
                    name="locality"
                    type="text"
                    placeholder="e.g. Sector 57, Sohna Road, Punhana"
                    required
                    value={values.locality}
                    onChange={onInput}
                  />
                )}
              </FormField>

              <OptionChips
                name="need"
                legend="What are you interested in?"
                options={NEED_OPTIONS}
                value={values.need}
                onChange={update}
                error={err('need')}
              />

              <FormField id="load" label="Approximate load in kW" optional hint="As shown on your electricity bill, if known.">
                {(a11y) => (
                  <input {...a11y} name="load" type="number" min="0" step="any" inputMode="decimal" value={values.load} onChange={onInput} />
                )}
              </FormField>
              <FormField id="language" label="Updates in">
                {(a11y) => (
                  <select {...a11y} name="language" value={values.language} onChange={onInput}>
                    <option>English</option>
                    <option>Hindi</option>
                  </select>
                )}
              </FormField>
            </div>
          </fieldset>

          <hr className="rule" />

          <div className={`field consent-field${errors.consent ? ' is-invalid' : ''}`} data-field="consent">
            <label className="consent">
              <input
                id="consent"
                name="consent"
                type="checkbox"
                required
                checked={values.consent}
                onChange={onInput}
                aria-invalid={errors.consent ? true : undefined}
                aria-describedby={errors.consent ? 'consent-error' : undefined}
              />
              <span>
                I agree that Eleven Power Private Limited may store the details above and contact me by phone, SMS, WhatsApp or email with updates about its network and services in my area. My details will be used only for this purpose, will not be shared with third parties, and I can withdraw my consent at any time by writing to{' '}
                <a href="mailto:connect@elevenpower.in">connect@elevenpower.in</a>.
              </span>
            </label>
            {errors.consent && (
              <p className="field__error" id="consent-error">
                {ERROR_MESSAGES.consent}
              </p>
            )}
          </div>

          {/* Spam trap: leave hidden */}
          <input
            ref={honeypotRef}
            className="visually-hidden-trap"
            type="text"
            name="company_website"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
          />

          {status === 'error' && (
            <p className="form-error" role="alert">
              Sorry, something went wrong. Please try again.
            </p>
          )}

          <button className="submit" type="submit" id="submit" disabled={submitting} aria-busy={submitting}>
            {submitting ? 'Submitting...' : 'Register interest'}
          </button>
          <p className="fine">
            Eleven Power Private Limited holds Distribution and Retail Supply Licence No. DRS-3 of 2026 granted by the Haryana Electricity Regulatory Commission for the Gurugram and Nuh revenue districts. Registering interest is not binding on you in any way.
          </p>
        </form>
      )}
    </section>
  );
}
